<button
  pButton
  type="button"
  severity="secondary"
  [text]="true"
  (click)="optionsPopover.toggle($event)"
  [attr.aria-label]="'chart.options.button' | translate"
>
  <i class="fa-solid fa-ellipsis-vertical"></i>
</button>

<p-popover #optionsPopover>
  <div class="flex flex-col gap-3 w-72">
    <span class="text-sm font-semibold text-surface-700 dark:text-surface-300">
      @if (overlayKey() && primaryAxisText()) {
        {{ 'chart.options.yAxis.left' | translate }}: {{ primaryAxisText() }}
      } @else {
        {{ 'chart.options.yAxis.title' | translate }}
      }
    </span>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <p-radiobutton
          inputId="yAxisMode-autoData-{{ chartId() }}"
          name="yAxisMode-{{ chartId() }}"
          value="auto-data"
          [ngModel]="config().yAxisMode"
          (ngModelChange)="onModeChange($event)"
        />
        <label for="yAxisMode-autoData-{{ chartId() }}" class="text-sm">
          {{ 'chart.options.yAxis.autoData' | translate }}
        </label>
      </div>

      @if (hasBands()) {
        <div class="flex items-center gap-2">
          <p-radiobutton
            inputId="yAxisMode-autoBands-{{ chartId() }}"
            name="yAxisMode-{{ chartId() }}"
            value="auto-bands"
            [ngModel]="config().yAxisMode"
            (ngModelChange)="onModeChange($event)"
          />
          <label for="yAxisMode-autoBands-{{ chartId() }}" class="text-sm">
            {{ 'chart.options.yAxis.autoBands' | translate }}
          </label>
        </div>
      }

      <div class="flex items-center gap-2">
        <p-radiobutton
          inputId="yAxisMode-manual-{{ chartId() }}"
          name="yAxisMode-{{ chartId() }}"
          value="manual"
          [ngModel]="config().yAxisMode"
          (ngModelChange)="onModeChange($event)"
        />
        <label for="yAxisMode-manual-{{ chartId() }}" class="text-sm">
          {{ 'chart.options.yAxis.manual' | translate }}
        </label>
      </div>
    </div>

    @if (config().yAxisMode === 'manual') {
      <div class="flex gap-2">
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <label class="text-xs text-surface-500">{{ 'chart.options.yAxis.min' | translate }}</label>
          <p-inputNumber
            [ngModel]="yMinDraft()"
            (ngModelChange)="yMinDraft.set($event); onManualRangeChange()"
            [maxFractionDigits]="6"
            fluid
          />
        </div>
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <label class="text-xs text-surface-500">{{ 'chart.options.yAxis.max' | translate }}</label>
          <p-inputNumber
            [ngModel]="yMaxDraft()"
            (ngModelChange)="yMaxDraft.set($event); onManualRangeChange()"
            [maxFractionDigits]="6"
            fluid
          />
        </div>
      </div>
      @if (manualRangeIncomplete()) {
        <span class="text-xs text-surface-500">
          {{ 'chart.options.yAxis.manualIncomplete' | translate }}
        </span>
      }
    }

    @if (overlayKey()) {
      <p-divider styleClass="!my-0" />

      <span class="text-sm font-semibold text-surface-700 dark:text-surface-300">
        {{ 'chart.options.yAxis.right' | translate }}: {{ overlayAxisText() }}
      </span>

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <p-radiobutton
            inputId="y2Mode-autoData-{{ chartId() }}"
            name="y2Mode-{{ chartId() }}"
            value="auto-data"
            [ngModel]="y2Mode()"
            (ngModelChange)="onOverlayModeChange($event)"
          />
          <label for="y2Mode-autoData-{{ chartId() }}" class="text-sm">
            {{ 'chart.options.yAxis.autoData' | translate }}
          </label>
        </div>
        <div class="flex items-center gap-2">
          <p-radiobutton
            inputId="y2Mode-manual-{{ chartId() }}"
            name="y2Mode-{{ chartId() }}"
            value="manual"
            [ngModel]="y2Mode()"
            (ngModelChange)="onOverlayModeChange($event)"
          />
          <label for="y2Mode-manual-{{ chartId() }}" class="text-sm">
            {{ 'chart.options.yAxis.manual' | translate }}
          </label>
        </div>
      </div>

      @if (y2Mode() === 'manual') {
        <div class="flex gap-2">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs text-surface-500">{{ 'chart.options.yAxis.min' | translate }}</label>
            <p-inputNumber
              [ngModel]="y2MinDraft()"
              (ngModelChange)="y2MinDraft.set($event); onOverlayRangeChange()"
              [maxFractionDigits]="6"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs text-surface-500">{{ 'chart.options.yAxis.max' | translate }}</label>
            <p-inputNumber
              [ngModel]="y2MaxDraft()"
              (ngModelChange)="y2MaxDraft.set($event); onOverlayRangeChange()"
              [maxFractionDigits]="6"
              fluid
            />
          </div>
        </div>
        @if (y2Incomplete()) {
          <span class="text-xs text-surface-500">
            {{ 'chart.options.yAxis.manualIncomplete' | translate }}
          </span>
        }
      }
    }

    @if (sameOriginAllowed()) {
      <p-divider styleClass="!my-0" />

      <span class="text-sm font-semibold text-surface-700 dark:text-surface-300">
        {{ 'chart.options.xAxis.title' | translate }}
      </span>

      <div class="flex items-center gap-2">
        <p-checkbox
          inputId="sameOrigin-{{ chartId() }}"
          [binary]="true"
          [ngModel]="config().sameOrigin ?? false"
          (ngModelChange)="onSameOriginChange($event)"
        />
        <label for="sameOrigin-{{ chartId() }}" class="text-sm">
          {{ 'chart.options.xAxis.sameOrigin' | translate }}
        </label>
      </div>
    }
  </div>
</p-popover>
